import { Link } from "react-router-dom";
import PageLayout from "../components/layout/PageLayout";
import { HERO_IMAGES, LOGO_METAL } from "../lib/constants";
import { Trophy, Shield, Zap, Users } from "lucide-react";

const FOUNDERS = [
    {
        name: "TORIANO SANZONE",
        role: "Co-Founder · Brand Strategist · Entrepreneur",
        bio: "Toriano Sanzone is a marketing professional and community leader whose work sits at the intersection of brand strategy, entrepreneurship and athlete development. He leads Digital Beast's brand architecture — helping athletes translate performance and personality into recognizable identities that carry beyond the field of play.",
        philosophy: "Every athlete has a story worth building a business around. My job is to help them tell it — and monetize it — without compromising who they are.",
        pillars: ["Brand Strategy", "Marketing", "Entrepreneurship", "Community Leadership"],
        img: HERO_IMAGES.stadium_lights,
    },
    {
        name: "TOI BAYLOR",
        role: "Co-Founder · Athlete Relations · NIL Management",
        bio: "Toi Baylor is a sports advocate and NIL-management professional with a deep athletic network spanning high school, college and professional pipelines. She anchors Digital Beast's athlete-facing work: recruitment, family relationships, school partnerships and long-term development.",
        philosophy: "Representation is not just about deals. It's about protecting the athlete's future — legally, financially, and personally. We show up for our people.",
        pillars: ["Athlete Development", "NIL Management", "Recruitment", "Family Advocacy"],
        img: HERO_IMAGES.training,
    },
];

export default function About() {
    return (
        <PageLayout>
            <section className="relative min-h-[52vh] flex items-end pb-16 pt-32 border-b border-white/10 overflow-hidden">
                <img src={HERO_IMAGES.huddle} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 w-full">
                    <div className="section-label mb-6">About Digital Beast</div>
                    <h1 className="font-display text-5xl md:text-7xl text-white leading-[0.95] tracking-tight max-w-4xl">
                        BUILT BY OPERATORS.<br/>
                        BUILT FOR <span className="text-[#d4af37]">ATHLETES.</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-white/60 text-lg leading-relaxed">
                        Digital Beast is where athletic performance becomes influence, influence becomes opportunity, and opportunity becomes legacy.
                    </p>
                </div>
            </section>

            <section className="py-20 md:py-28">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-start">
                    <div>
                        <div className="section-label mb-5">Our Mission</div>
                        <h2 className="font-display text-3xl md:text-4xl text-white leading-tight tracking-tight">
                            Turn talent into a brand.<br/> Turn a brand into a business.
                        </h2>
                        <p className="mt-6 text-white/60 leading-relaxed">
                            Digital Beast provides athlete branding, social media management, sponsorship development and NIL representation for high school and collegiate athletes ready to build influence beyond the game. We combine the discipline of a premium sports agency, the creativity of an athlete-branding studio, and the operational muscle of a social media command center — all under one roof.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        {[
                            { icon: Trophy, l: "Athlete-Centered" },
                            { icon: Shield, l: "Compliance-First" },
                            { icon: Zap, l: "Growth-Focused" },
                            { icon: Users, l: "Family-Driven" },
                        ].map(({icon: I, l}) => (
                            <div key={l} className="surface p-6" data-testid={`about-pillar-${l.toLowerCase()}`}>
                                <I className="text-[#d4af37]" size={24} strokeWidth={1.5} />
                                <div className="font-display text-lg text-white mt-4">{l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 border-t border-white/10 bg-[#0a0a0a]" data-testid="founders-section">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="section-label mb-6">The Founders</div>
                    <h2 className="font-display text-4xl md:text-5xl text-white mb-14 tracking-tight">Managing Agents</h2>
                    <div className="space-y-16">
                        {FOUNDERS.map((f, i) => (
                            <div key={f.name} className="grid lg:grid-cols-12 gap-8 items-start" data-testid={`founder-${i}`}>
                                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                                    <div className="aspect-[4/5] overflow-hidden surface relative">
                                        <img src={f.img} alt={f.name} className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                                        <div className="absolute bottom-6 left-6 right-6">
                                            <div className="font-display text-3xl md:text-4xl text-white leading-none">{f.name}</div>
                                            <div className="text-[#d4af37] text-xs uppercase tracking-[0.24em] mt-2">{f.role}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="lg:col-span-7">
                                    <p className="text-white/70 text-lg leading-relaxed">{f.bio}</p>
                                    <div className="mt-8 border-l-2 border-[#d4af37] pl-5">
                                        <p className="font-display text-xl md:text-2xl text-white italic leading-snug">"{f.philosophy}"</p>
                                    </div>
                                    <div className="mt-8 flex flex-wrap gap-2">
                                        {f.pillars.map(p => (
                                            <span key={p} className="chip">{p}</span>
                                        ))}
                                    </div>
                                    <div className="mt-8 flex gap-3">
                                        <Link to="/contact" className="btn-outline" data-testid={`founder-contact-${i}`}>Book Consultation</Link>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 text-center">
                    <img src={LOGO_METAL} alt="" className="h-16 mx-auto opacity-80 mb-6" />
                    <h3 className="font-display text-3xl md:text-5xl text-white max-w-3xl mx-auto tracking-tight">
                        We don't just sign athletes.<br/> We build <span className="text-[#d4af37]">empires</span>.
                    </h3>
                    <div className="mt-8 flex justify-center gap-3">
                        <Link to="/apply" className="btn-gold" data-testid="about-cta-apply">Apply for Representation</Link>
                        <Link to="/contact" className="btn-outline" data-testid="about-cta-contact">Contact the Founders</Link>
                    </div>
                </div>
            </section>
        </PageLayout>
    );
}
